import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getCampagneAdmin, updateCampagne, updateLiensPartage, revoquerLiensPartage,
} from '../../services/api';

const STATUTS_CAMPAGNE = {
  a_venir: { label: 'À venir', className: 'a-venir' },
  ouverte: { label: 'Dépôts ouverts', className: 'ouverte' },
  cloturee: { label: 'Clôturée', className: 'cloturee' },
};

// L'API renvoie l'heure de N'Djaména ("2026-10-15T23:59:59+01:00") : on garde "AAAA-MM-JJTHH:MM"
const versChamp = (iso) => (iso || '').slice(0, 16);

const Parametres = () => {
  const [campagne, setCampagne] = useState(null);
  const [form, setForm] = useState({ date_ouverture: '', date_cloture: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const charger = (c) => {
    setCampagne(c);
    setForm({ date_ouverture: versChamp(c.date_ouverture), date_cloture: versChamp(c.date_cloture) });
  };

  useEffect(() => {
    getCampagneAdmin().then(charger).catch(() => toast.error('Impossible de charger la période de dépôt.'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.date_cloture <= form.date_ouverture) {
      setErrors({ date_cloture: "La clôture doit être postérieure à l'ouverture." });
      return;
    }
    setSaving(true);
    setErrors({});
    try {
      // Heures saisies en heure de N'Djaména (fuseau du serveur) ; clôture jusqu'à la dernière seconde
      const c = await updateCampagne({
        date_ouverture: `${form.date_ouverture}:00`,
        date_cloture: `${form.date_cloture}:59`,
      });
      charger(c);
      toast.success('Période de dépôt mise à jour.');
    } catch (err) {
      const data = err.response?.data || {};
      setErrors(Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : String(v)]),
      ));
      toast.error("L'enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  if (!campagne) {
    return <div className="loader"><div className="spinner" /></div>;
  }

  const statut = STATUTS_CAMPAGNE[campagne.statut] || STATUTS_CAMPAGNE.ouverte;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Paramètres</h1>
          <span className="muted" style={{ fontSize: '0.85rem' }}>Période de dépôt des candidatures</span>
        </div>
      </div>

      <form className="detail-card settings-card" onSubmit={handleSubmit}>
        <div className="detail-header">
          <h3>Période de dépôt</h3>
          <span className={`campagne-badge ${statut.className}`}>{statut.label}</span>
        </div>
        <div className="detail-body">
          <p className="settings-current">
            Actuellement : du <strong>{campagne.libelle_ouverture}</strong> au <strong>{campagne.libelle_cloture}</strong>.
          </p>

          <div className="form-row two-cols">
            <div className="form-group">
              <label htmlFor="date_ouverture">Ouverture des dépôts</label>
              <input
                id="date_ouverture"
                type="datetime-local"
                required
                value={form.date_ouverture}
                className={errors.date_ouverture ? 'error' : ''}
                onChange={(e) => setForm(f => ({ ...f, date_ouverture: e.target.value }))}
              />
              {errors.date_ouverture && <div className="error-message">{errors.date_ouverture}</div>}
            </div>
            <div className="form-group">
              <label htmlFor="date_cloture">Clôture des dépôts</label>
              <input
                id="date_cloture"
                type="datetime-local"
                required
                value={form.date_cloture}
                className={errors.date_cloture ? 'error' : ''}
                onChange={(e) => setForm(f => ({ ...f, date_cloture: e.target.value }))}
              />
              {errors.date_cloture && <div className="error-message">{errors.date_cloture}</div>}
            </div>
          </div>

          <div className="notice">
            Heures de N'Djaména. Les changements s'appliquent immédiatement : hors de cette période,
            le formulaire public affiche « bientôt ouvert » ou « clôturé » et le serveur refuse tout dépôt.
            Une clôture à 23:59 s'affiche « à minuit ».
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </form>

      <LiensPartage campagne={campagne} onChange={setCampagne} />
    </>
  );
};

// Liens des pièces jointes insérés dans l'export Excel, ouvrables sans connexion
const LiensPartage = ({ campagne, onChange }) => {
  const [jours, setJours] = useState(String(campagne.liens_validite_jours));
  const [saving, setSaving] = useState(false);

  const enregistrer = async (e) => {
    e.preventDefault();
    const n = parseInt(jours, 10);
    if (!(n >= 1 && n <= 365)) {
      toast.error('Durée entre 1 et 365 jours.');
      return;
    }
    setSaving(true);
    try {
      onChange(await updateLiensPartage(n));
      toast.success('Durée de validité mise à jour.');
    } catch {
      toast.error("L'enregistrement a échoué.");
    } finally {
      setSaving(false);
    }
  };

  const revoquer = async () => {
    if (!window.confirm(
      'Désactiver tous les liens de documents des fichiers Excel déjà transmis ?\n'
      + 'Les destinataires devront recevoir un nouvel export.',
    )) return;
    try {
      onChange(await revoquerLiensPartage());
      toast.success('Tous les liens déjà partagés sont désactivés.');
    } catch {
      toast.error('La révocation a échoué.');
    }
  };

  return (
    <form className="detail-card settings-card" onSubmit={enregistrer} style={{ marginTop: '1rem' }}>
      <div className="detail-header">
        <h3>Liens des documents (export Excel)</h3>
      </div>
      <div className="detail-body">
        <p className="settings-current">
          Les colonnes « URL » de l'export Excel ouvrent les pièces jointes <strong>sans connexion</strong>,
          pour toute personne qui reçoit le fichier, pendant la durée ci-dessous à compter de l'export.
        </p>
        <div className="form-group" style={{ maxWidth: 260 }}>
          <label htmlFor="liens_validite_jours">Durée de validité (jours)</label>
          <input
            id="liens_validite_jours"
            type="number"
            min="1"
            max="365"
            required
            value={jours}
            onChange={(e) => setJours(e.target.value)}
          />
        </div>
        <div className="notice warn">
          Ces fichiers donnent accès à des pièces d'identité : ne les transmettez qu'aux personnes habilitées.
          En cas de diffusion non souhaitée, révoquez les liens : les fichiers déjà envoyés ne permettront
          plus d'ouvrir les documents.
        </div>
        <div className="actions">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
          <button className="btn btn-danger" type="button" onClick={revoquer}>
            Révoquer tous les liens déjà partagés
          </button>
        </div>
      </div>
    </form>
  );
};

export default Parametres;
